import * as React from 'react'
import { createFileRoute,Outlet,useNavigate,useSearch } from '@tanstack/react-router'
import { z } from 'zod'
import { useQuery } from '@tanstack/react-query'
import { requireAuth } from '@/lib/auth'
import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import {
Table,
TableBody,
TableCell,
TableHead,
TableHeader,
TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Plus,Search,X,RefreshCw,MoreHorizontal,Pencil,Trash2,CheckCircle2,XCircle } from 'lucide-react'
import {
DropdownMenu,
DropdownMenuContent,
DropdownMenuItem,
DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { PageBreadcrumb } from '@/components/dashboard/page-breadcrumb'
import { Skeleton } from '@/components/ui/skeleton'
import { DataTablePagination } from '@/components/dashboard/data-table-pagination'
import { queryKeys } from '@/lib/query-keys'
import { getPrintAgents,regeneratePairCode,regenerateAgentKey } from '@/features/devices/api'
import { useSelectedShopId } from '@/features/shared'
import type { PrintAgent } from '@/types'
import { AgentDetailCard } from '@/features/devices/agent-detail-card'
import { AgentStatusBadge } from '@/features/devices/agent-status-badge'
import { toast } from 'sonner'

const CreateAgentDialog = React.lazy(() => import('@/features/devices/create-agent-dialog').then(m => ({ default: m.CreateAgentDialog })))
const PairCodeDialog = React.lazy(() => import('@/features/devices/pair-code-dialog').then(m => ({ default: m.PairCodeDialog })))
const RegenerateKeyDialog = React.lazy(() => import('@/features/devices/regenerate-key-dialog').then(m => ({ default: m.RegenerateKeyDialog })))
const UnpairAgentDialog = React.lazy(() => import('@/features/devices/unpair-agent-dialog').then(m => ({ default: m.UnpairAgentDialog })))
const DeleteAgentDialog = React.lazy(() => import('@/features/devices/delete-agent-dialog').then(m => ({ default: m.DeleteAgentDialog })))

const searchSchema = z.object({
  page: z.number().int().min(1).catch(1),
  search: z.string().optional(),
  limit: z.number().int().min(1).catch(20),
})

let suppressedAgentsAutoOpenSignature: string | null = null

export const Route = createFileRoute('/_dashboard/devices/agents')({
  validateSearch: searchSchema,
  beforeLoad: requireAuth(),
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: AgentsLayout,
})

function AgentsLayout() {
  return <Outlet />
}

export function AgentsPage({ selectedId }: { selectedId: string | undefined }) {
  const searchParams = useSearch({ from: Route.id })
  const navigate = useNavigate()
  const shopId = useSelectedShopId()

  const [searchTerm, setSearchTerm] = React.useState(searchParams.search || '')
  
  const [isCreateOpen, setIsCreateOpen] = React.useState(false)
  const [editingAgent, setEditingAgent] = React.useState<PrintAgent | null>(null)
  
  const [pairCode, setPairCode] = React.useState<string | null>(null)
  const [apiKey, setApiKey] = React.useState<string | null>(null)
  
  const [unpairingAgent, setUnpairingAgent] = React.useState<PrintAgent | null>(null)
  const [deletingAgent, setDeletingAgent] = React.useState<PrintAgent | null>(null)

  const page = searchParams.page
  const limit = searchParams.limit
  const pageSignature = `${shopId ?? 'no-shop'}:${page}:${limit}:${searchParams.search ?? ''}`

  const [isRefreshing, setIsRefreshing] = React.useState(false)

  const agentsQuery = useQuery({
    queryKey: queryKeys.printAgents(shopId ?? '', { page, limit, search: searchParams.search }),
    queryFn: () => getPrintAgents(shopId!, { page, limit, search: searchParams.search }),
    enabled: !!shopId,
    refetchInterval: 10_000,
  })

  const agents = React.useMemo(
    () => agentsQuery.data?.data ?? [],
    [agentsQuery.data?.data],
  )
  const isLoading = agentsQuery.isLoading
  const meta = agentsQuery.data?.meta ?? { total: 0, page: 1, limit: 20, totalPages: 1 }

  const currentPageHasSelectedAgent = React.useMemo(
    () => !!selectedId && agents.some((agent) => agent.id === selectedId),
    [agents, selectedId],
  )
  const effectiveSelectedId = React.useMemo(() => {
    if (currentPageHasSelectedAgent) return selectedId
    if (isLoading || agents.length === 0) return undefined
    if (!selectedId && suppressedAgentsAutoOpenSignature === pageSignature) return undefined
    return agents[0].id
  }, [agents, currentPageHasSelectedAgent, isLoading, pageSignature, selectedId])
  const selectedAgent = React.useMemo(
    () => agents.find((a) => a.id === effectiveSelectedId),
    [agents, effectiveSelectedId]
  )

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    navigate({
      to: '/devices/agents',
      search: (prev) => ({ ...prev, page: 1, search: searchTerm || undefined }),
    })
  }

  const setSelectedId = React.useCallback(
    (id: string | null) => {
      if (id) {
        navigate({
          to: '/devices/agents/$item',
          params: { item: id },
          search: (prev) => ({ ...prev }),
        })
        return
      }
      navigate({
        to: '/devices/agents',
        search: (prev) => ({ ...prev }),
      })
    },
    [navigate]
  )

  const closeSelectedAgent = React.useCallback(() => {
    suppressedAgentsAutoOpenSignature = pageSignature
    setSelectedId(null)
  }, [pageSignature, setSelectedId])

  React.useEffect(() => {
    if (!effectiveSelectedId || effectiveSelectedId === selectedId) return
    setSelectedId(effectiveSelectedId)
  }, [effectiveSelectedId, selectedId, setSelectedId])

  React.useEffect(() => {
    return () => {
      window.setTimeout(() => {
        if (!window.location.pathname.startsWith('/devices/agents')) {
          suppressedAgentsAutoOpenSignature = null
        }
      }, 0)
    }
  }, [])

  const handleCreateSuccess = (agent: PrintAgent & { pairCode?: string | null }) => {
    if (!editingAgent && agent.pairCode) {
      setPairCode(agent.pairCode)
    }
  }

  const handleRegeneratePairCode = async () => {
    if (!selectedId) return
    try {
      const res = await regeneratePairCode(selectedId)
      setPairCode(res.pairCode)
    } catch {
      toast.error('Failed to regenerate pair code')
    }
  }

  const handleRegenerateKey = async () => {
    if (!selectedId) return
    try {
      const res = await regenerateAgentKey(selectedId)
      setApiKey(res.apiKey)
    } catch {
      toast.error('Failed to regenerate API key')
    }
  }

  function handleRefresh() {
    setIsRefreshing(true)
    void agentsQuery.refetch().then(() => setTimeout(() => setIsRefreshing(false), 600))
  }

  return (
    <div className="space-y-6">
      <PageBreadcrumb />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Print Agents</h2>
          <p className="text-muted-foreground">Manage the background services powering your printers.</p>
        </div>
        <Button
          onClick={() => {
            setEditingAgent(null)
            setIsCreateOpen(true)
          }}
        >
          <Plus className="mr-2 h-4 w-4" /> Add Agent
        </Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <form onSubmit={handleSearch} className="relative flex-1 max-w-sm group">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search agents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 pr-8"
          />
          {searchTerm && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={() => {
                setSearchTerm('')
                navigate({ to: '/devices/agents', search: (prev) => ({ ...prev, page: 1, search: undefined }) })
              }}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </form>
        <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={handleRefresh}>
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div className={`flex-1 min-w-0 transition-all duration-300 ${!isLoading && agents.length === 0 ? '' : 'border rounded-md squircle bg-card shadow-sm overflow-hidden'}`}>
          {!isLoading && agents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 gap-6 text-center">
              <div className="relative w-24 h-24 opacity-10 grayscale shrink-0">
                <img src="/misc/logo.png" alt="Rootpoint Logo" className="w-full h-full object-contain" />
              </div>
              <div className="flex flex-col gap-4 items-center">
                <p className="font-medium text-muted-foreground text-lg">No print agents found</p>
              </div>
            </div>
          ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead className="w-[250px]">Name</TableHead>
                <TableHead>Paired</TableHead>
                <TableHead>Platform</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-[150px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
                    <TableCell><Skeleton className="h-8 w-8 ml-auto" /></TableCell>
                  </TableRow>
                ))
              ) : (
                agents.map((agent) => (
                  <TableRow
                    key={agent.id}
                    className="cursor-pointer hover:bg-muted/50 data-[state=selected]:bg-muted data-[state=selected]:hover:bg-muted/80 group"
                    data-state={effectiveSelectedId === agent.id ? 'selected' : undefined}
                    onClick={() => effectiveSelectedId === agent.id ? closeSelectedAgent() : setSelectedId(agent.id)}
                  >
                    <TableCell className="font-medium text-sm">
                      <div className="flex items-center gap-2">
                        <span className="truncate">{agent.name}</span>
                        <AgentStatusBadge status={agent.status} dotOnly />
                      </div>
                    </TableCell>
                    <TableCell>
                      {(agent.isPaired || agent.status === 'ONLINE') ? (
                        <div className="flex items-center text-green-600 text-sm font-medium">
                          <CheckCircle2 className="mr-1 h-4 w-4" /> Yes
                        </div>
                      ) : (
                        <div className="flex items-center text-muted-foreground text-sm font-medium">
                          <XCircle className="mr-1 h-4 w-4" /> No
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {agent.platform || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation()
                              setEditingAgent(agent)
                              setIsCreateOpen(true)
                            }}
                          >
                            <Pencil className="h-4 w-4" /> Edit Name
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive focus:bg-destructive/10"
                            onClick={(e) => {
                              e.stopPropagation()
                              setDeletingAgent(agent)
                            }}
                          >
                            <Trash2 className="h-4 w-4" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          )}
          {!isLoading && agents.length > 0 && (
            <DataTablePagination
              page={page}
              itemsPerPage={limit}
              totalItems={meta.total}
              totalPages={meta.totalPages}
              isLoading={isLoading}
              entityName="agents"
              onPageChange={(p) =>
                navigate({
                  to: '.',
                  search: (prev) => ({ ...prev, page: p }),
                  replace: true,
                })
              }
              onLimitChange={(l) =>
                navigate({
                  to: '.',
                  search: (prev) => ({ ...prev, page: 1, limit: l }),
                  replace: true,
                })
              }
            />
          )}
        </div>

        {selectedAgent && (
          <AgentDetailCard
            agent={selectedAgent}
            onClose={closeSelectedAgent}
            onRegeneratePairCode={handleRegeneratePairCode}
            onRegenerateKey={handleRegenerateKey}
            onUnpair={() => setUnpairingAgent(selectedAgent)}
            onEdit={() => {
              setEditingAgent(selectedAgent)
              setIsCreateOpen(true)
            }}
            onDelete={() => setDeletingAgent(selectedAgent)}
          />
        )}
      </div>

      <React.Suspense fallback={null}>
        {isCreateOpen && (
          <CreateAgentDialog
            isOpen={isCreateOpen}
            onClose={() => {
              setIsCreateOpen(false)
              setEditingAgent(null)
            }}
            agent={editingAgent}
            onSuccess={handleCreateSuccess}
          />
        )}
        {pairCode && (
          <PairCodeDialog
            isOpen={!!pairCode}
            onClose={() => setPairCode(null)}
            pairCode={pairCode}
          />
        )}
        {apiKey && (
          <RegenerateKeyDialog
            isOpen={!!apiKey}
            onClose={() => setApiKey(null)}
            apiKey={apiKey}
          />
        )}
        {unpairingAgent && (
          <UnpairAgentDialog
            isOpen={!!unpairingAgent}
            onClose={() => setUnpairingAgent(null)}
            agent={unpairingAgent}
          />
        )}
        {deletingAgent && (
          <DeleteAgentDialog
            isOpen={!!deletingAgent}
            onClose={() => {
              setDeletingAgent(null)
              if (effectiveSelectedId === deletingAgent.id) closeSelectedAgent()
            }}
            agent={deletingAgent}
          />
        )}
      </React.Suspense>
    </div>
  )
}
